import React, { useEffect, useRef, useState } from 'react';
import { PasswordTextField } from '../passwordTextField/PasswordTextField';
import { StrongPasswordCheck } from './strong-password-check';
import { Popper } from '@workday/canvas-kit-react/popup';
import styles from './PasswordTextFieldWithConfirm.module.scss';
import { SystemIcon, SystemIconCircleSize } from '@workday/canvas-kit-react/icon';
import { colors } from '@workday/canvas-kit-react/tokens';
import { checkCircleIcon } from '@workday/canvas-system-icons-web';
import { ID_CONFIRM_PASSWORD, ID_PASSWORD } from '../../autoIds';
import { useTranslation } from 'react-i18next';

export const passwordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*\W)(?=.{8,50})/;

interface Validation {
    isValid: boolean;
    errors: {
        password?: string;
        confirmPassword?: string;
    };
}

interface PasswordTextFieldWithConfirmProps {
    className?: 'string';
    isDisabled: boolean;
    passwordUpdated: (boolean, string) => void;
    showPasswordErrors: boolean;
    setShowPasswordErrors: (boolean) => void;
    shouldAutoFocus?: boolean;
}

export function PasswordTextFieldWithConfirm({
    className,
    isDisabled,
    passwordUpdated,
    showPasswordErrors,
    setShowPasswordErrors,
    shouldAutoFocus = false
}: PasswordTextFieldWithConfirmProps) {
    const { t } = useTranslation('shared');
    const passwordInputRef = useRef();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const requiredError = password => (password.length === 0 ? t('Required.fieldGeneric') : undefined);
    const validError = password => (!passwordRegex.test(password) ? t('ChangePassword.invalidPassword') : undefined);
    const noMatchError = (confirmPassword, password) => {
        return !requiredError(password) && !validError(password) && password !== confirmPassword
            ? t('ChangePassword.passwordDoNotMatch')
            : undefined;
    };

    const invalidPassword = password ? !!validError(password) : undefined;

    // order is important
    const passwordErrors = [requiredError, validError];
    const confirmPasswordErrors = [...passwordErrors, noMatchError];
    function createValidation(password, confirmPassword): Validation {
        // execute all the validations and find the nearest errors
        const passwordError = passwordErrors.map(error => error(password)).find(Boolean);
        const confirmPasswordError = confirmPasswordErrors.map(error => error(confirmPassword, password)).find(Boolean);

        return {
            isValid: !passwordError && !confirmPasswordError,
            errors: {
                password: passwordError,
                confirmPassword: confirmPasswordError
            }
        };
    }

    const { isValid, errors } = createValidation(password, confirmPassword);

    useEffect(() => {
        setShowPasswordErrors(false);
    }, [password, confirmPassword, setShowPasswordErrors]);

    useEffect(() => {
        passwordUpdated(isValid, password);
    }, [isValid, password, passwordUpdated]);

    function onPasswordChange(e) {
        setPassword(e.target.value);
    }

    function onConfirmPasswordChange(e) {
        setConfirmPassword(e.target.value);
    }

    return (
        <div className={className}>
            <PasswordTextField
                passwordInputRef={passwordInputRef}
                autoFocus={shouldAutoFocus}
                autoComplete='new-password'
                errorMessage={showPasswordErrors ? errors.password : undefined}
                handlers={{
                    onChange: onPasswordChange
                }}
                id={ID_PASSWORD}
                isDisabled={isDisabled}
                isInvalid={showPasswordErrors && !!errors.password}
                placeholder={t('ChangePassword.placeHolder')}
                label={t('ChangePassword.newPassword')}
                errorLabel={t('Required.fieldErrorLabel')}
                value={password}
            />
            <Popper open={!!invalidPassword} anchorElement={passwordInputRef.current} portal={false}>
                <StrongPasswordCheck password={password} />
            </Popper>
            <PasswordTextField
                autoComplete='new-password'
                errorMessage={showPasswordErrors ? errors.confirmPassword : undefined}
                handlers={{ onChange: onConfirmPasswordChange }}
                id={ID_CONFIRM_PASSWORD}
                isDisabled={isDisabled}
                isInvalid={showPasswordErrors && !!errors.confirmPassword}
                placeholder={t('ChangePassword.placeHolder')}
                label={t('ChangePassword.confirmNewPassword')}
                errorLabel={t('Required.fieldErrorLabel')}
                value={confirmPassword}
            />
            {isValid && (
                <div className={styles.successMessage}>
                    <SystemIcon
                        className={styles.successIcon}
                        color={colors.blueberry500}
                        colorHover={colors.blueberry500}
                        icon={checkCircleIcon}
                        size={SystemIconCircleSize.s}
                    />
                    {t('ChangePassword.confirmPasswordMatch')}
                </div>
            )}
        </div>
    );
}
